import React, { useCallback, useMemo, useState } from "react";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Box,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import ContactsIcon from "@mui/icons-material/Contacts";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

import {
  TABLE_ICON_BLUE_SX,
  TABLE_EXPLORE_MENU_ITEM_SX,
  TABLE_ICON_GREEN_SX,
  TABLE_ROW_HOVER_SX,
  TABLE_STYLES,
} from "../../theme/tablestyles";
import { SELECT_MENU_PROPS } from "../../theme/filterstyles";
import {
  formatNullableBoolean,
  formatNullableNumber,
} from "../../utils/formatters";

const COLUMN_WIDTHS = {
  concept: 240,
  openings_closings_next_12_months: 210,
  unit_count_end_2025: 120,
  typical_store_size_median: 180,
  retail_sector: 160,
  retail_subsector: 180,
  country: 120,
  explore: 88,
};
const HEAD_CELL_SX = {
  backgroundColor: "#00336C",
  color: "#ffffff",
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  whiteSpace: "nowrap",
  borderBottom: "none",
  py: 1.5,
};

const HEADER_TRIGGER_SX = {
  minWidth: 108,
  minHeight: 28,
  px: 1.5,
  py: 0.35,
  fontWeight: 700,
  fontSize: "0.72rem",
  color: "#00336C",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  borderRadius: 1.5,
  border: "1.5px solid rgba(255,255,255,0.35)",
  backgroundColor: "rgba(255,255,255,0.92)",
  "&:hover": { backgroundColor: "#ffffff", borderColor: "rgba(255,255,255,0.8)" },
};
const FILTER_MENU_WIDTH = 360;
const FILTER_MENU_MAX_WIDTH = "calc(100vw - 32px)";
const SEARCH_FIELD_SX = {
  px: 1,
  pt: 0.75,
  pb: 0.25,
  backgroundColor: "white",
  position: "sticky",
  top: 0,
  zIndex: 1,
};
const FILTER_SEARCH_INPUT_SX = {
  "& .MuiInputBase-root": {
    fontSize: "0.875rem",
    backgroundColor: "#ffffff",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
  },
};

const COLUMNS = [
  { id: "concept", label: "Company" },
  {
    id: "openings_closings_next_12_months",
    label: "Openings/Closings",
    sortable: true,
  },
  { id: "unit_count_end_2025", label: "Unit Count" },
  { id: "typical_store_size_median", label: "Typical Store Size Sq Ft" },
  { id: "retail_sector", label: "Sector" },
  { id: "retail_subsector", label: "Subsector" },
  { id: "country", label: "Country" },
  { id: "explore", label: "Explore" },
];

function getRetailWebsiteUrl(row) {
  const website = row?.concept_website || row?.parent_company_website;

  if (!website) {
    return "";
  }

  return website.startsWith("http") ? website : `https://${website}`;
}

function getDisplayValue(row, columnId) {
  const value = row?.[columnId];

  if (
    columnId === "openings_closings_next_12_months" ||
    columnId === "unit_count_end_2025" ||
    columnId === "typical_store_size_min" ||
    columnId === "typical_store_size_max" ||
    columnId === "typical_store_size_median"
  ) {
    return formatNullableNumber(value, "—");
  }

  if (columnId === "franchise") {
    const booleanValue = formatNullableBoolean(value, "—");
    return booleanValue === "—" ? booleanValue : booleanValue ? "Yes" : "No";
  }

  return value || "—";
}

function hasRetailContact(row) {
  return Boolean(
    row?.contact_full_name ||
      row?.contact_first_name ||
      row?.contact_last_name ||
      row?.contact_title ||
      row?.contact_email ||
      row?.contact_phone ||
      row?.linkedin_url,
  );
}

export default function RetailTable({
  rows = [],
  orderBy,
  orderDirection = "desc",
  onSort,
  sectorFilter,
  onSectorFilterChange,
  sectorOptions,
  subsectorFilter,
  onSubsectorFilterChange,
  subsectorOptions,
  countryFilter,
  onCountryFilterChange,
  countryOptions,
  onOpenProfile,
  onOpenContactProfile,
}) {
  const [exploreAnchorEl, setExploreAnchorEl] = useState(null);
  const [activeExploreRow, setActiveExploreRow] = useState(null);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [filterSearch, setFilterSearch] = useState("");

  const handleOpenExploreMenu = useCallback((event, row) => {
    setExploreAnchorEl(event.currentTarget);
    setActiveExploreRow(row);
  }, []);

  const handleCloseExploreMenu = useCallback(() => {
    setExploreAnchorEl(null);
    setActiveExploreRow(null);
  }, []);

  const openFilterMenu = (event, filterKey) => {
    setFilterAnchor(event.currentTarget);
    setActiveFilter(filterKey);
    setFilterSearch("");
  };

  const closeFilterMenu = () => {
    setFilterAnchor(null);
    setActiveFilter(null);
    setFilterSearch("");
  };

  const filteredSectorOptions = useMemo(() => {
    const normalizedSearch = filterSearch.trim().toLowerCase();
    if (!normalizedSearch) return sectorOptions;
    return sectorOptions.filter((option) =>
      option.toLowerCase().includes(normalizedSearch),
    );
  }, [filterSearch, sectorOptions]);

  const filteredSubsectorOptions = useMemo(() => {
    const normalizedSearch = filterSearch.trim().toLowerCase();
    if (!normalizedSearch) return subsectorOptions;
    return subsectorOptions.filter((option) =>
      option.toLowerCase().includes(normalizedSearch),
    );
  }, [filterSearch, subsectorOptions]);

  const filteredCountryOptions = useMemo(() => {
    const normalizedSearch = filterSearch.trim().toLowerCase();
    if (!normalizedSearch) return countryOptions;
    return countryOptions.filter((option) =>
      option.toLowerCase().includes(normalizedSearch),
    );
  }, [filterSearch, countryOptions]);

  const exploreActions = useMemo(() => {
    if (!activeExploreRow) return [];

    return [
      {
        key: "profile",
        label: "Company Profile",
        icon: <BusinessIcon sx={{ ...TABLE_ICON_GREEN_SX, fontSize: 22 }} />,
        onClick: () => onOpenProfile?.(activeExploreRow),
      },
      {
        key: "contact",
        label: "Contact Profile",
        icon: (
          <ContactsIcon
            sx={{
              ...TABLE_ICON_BLUE_SX,
              fontSize: 22,
              opacity: hasRetailContact(activeExploreRow) ? 1 : 0.35,
            }}
          />
        ),
        disabled: !hasRetailContact(activeExploreRow),
        onClick: () => onOpenContactProfile?.(activeExploreRow),
      },
    ];
  }, [activeExploreRow, onOpenContactProfile, onOpenProfile]);

  const renderFilterHeader = ({
    label,
    filterKey,
    active = false,
  }) => {
    const minWidthByFilterKey = {
      retail_sector: 132,
      retail_subsector: 148,
      country: 124,
    };

    return (
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        justifyContent="center"
        sx={{ width: "100%" }}
      >
        <Button
          variant="text"
          onClick={(event) => openFilterMenu(event, filterKey)}
          sx={{
            ...HEADER_TRIGGER_SX,
            minWidth: minWidthByFilterKey[filterKey] || HEADER_TRIGGER_SX.minWidth,
            color: active ? "#648315" : "#00336C",
            border: active ? "1.5px solid #648315" : HEADER_TRIGGER_SX.border,
            backgroundColor: "rgba(255,255,255,0.92)",
            "&:hover": { backgroundColor: "#ffffff", borderColor: active ? "#648315" : "rgba(255,255,255,0.8)" },
          }}
          endIcon={<ArrowDropDownIcon sx={{ fontSize: "1.15rem" }} />}
        >
          {label}
        </Button>
      </Stack>
    );
  };

  const renderFilterMenu = () => {
    if (!activeFilter) return null;

    const commonMenuProps = {
      open: Boolean(filterAnchor),
      anchorEl: filterAnchor,
      onClose: closeFilterMenu,
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left",
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "left",
      },
      marginThreshold: 16,
      MenuListProps: {
        dense: true,
        sx: {
          py: 0,
          maxHeight: 520,
          width: FILTER_MENU_WIDTH,
          maxWidth: FILTER_MENU_MAX_WIDTH,
          boxSizing: "border-box",
          overflowX: "hidden",
        },
      },
      ...SELECT_MENU_PROPS,
      slotProps: {
        ...SELECT_MENU_PROPS.slotProps,
      },
      PaperProps: {
        ...SELECT_MENU_PROPS.PaperProps,
        sx: {
          ...(SELECT_MENU_PROPS.PaperProps?.sx || {}),
          width: FILTER_MENU_WIDTH,
          maxWidth: FILTER_MENU_MAX_WIDTH,
          boxSizing: "border-box",
          maxHeight: 560,
          overflowX: "hidden",
          overflowY: "auto",
          py: 0,
          "& .MuiTextField-root": {
            maxWidth: "100%",
          },
        },
      },
    };

    const searchField = (placeholder) => (
      <Box sx={SEARCH_FIELD_SX}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={filterSearch}
          onChange={(event) => setFilterSearch(event.target.value)}
          onKeyDown={(event) => event.stopPropagation()}
          placeholder={placeholder}
          sx={FILTER_SEARCH_INPUT_SX}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ mr: 1, color: "#00336C" }} />,
            },
          }}
        />
      </Box>
    );

    if (activeFilter === "retail_sector") {
      return (
        <Menu {...commonMenuProps}>
          {searchField("Search sectors")}
          <MenuItem
            selected={!sectorFilter}
            onClick={() => {
              onSectorFilterChange("");
              closeFilterMenu();
            }}
          >
            All Sectors
          </MenuItem>
          {filteredSectorOptions.map((option) => (
            <MenuItem
              key={option}
              selected={sectorFilter === option}
              onClick={() => {
                onSectorFilterChange(option);
                closeFilterMenu();
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      );
    }

    if (activeFilter === "retail_subsector") {
      return (
        <Menu {...commonMenuProps}>
          {searchField("Search subsectors")}
          <MenuItem
            selected={!subsectorFilter}
            onClick={() => {
              onSubsectorFilterChange("");
              closeFilterMenu();
            }}
          >
            All Subsectors
          </MenuItem>
          {filteredSubsectorOptions.map((option) => (
            <MenuItem
              key={option}
              selected={subsectorFilter === option}
              onClick={() => {
                onSubsectorFilterChange(option);
                closeFilterMenu();
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      );
    }

    if (activeFilter === "country") {
      return (
        <Menu {...commonMenuProps}>
          {searchField("Search countries")}
          <MenuItem
            selected={!countryFilter}
            onClick={() => {
              onCountryFilterChange("");
              closeFilterMenu();
            }}
          >
            All Countries
          </MenuItem>
          {filteredCountryOptions.map((option) => (
            <MenuItem
              key={option}
              selected={countryFilter === option}
              onClick={() => {
                onCountryFilterChange(option);
                closeFilterMenu();
              }}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      );
    }

    return null;
  };

  return (
    <>
      <TableContainer
        component="div"
        sx={{
          background: "#00336C",
          overflow: "hidden",
          width: "100%",
          overflowX: "auto",
        }}
      >
        <Table size="small" sx={{ minWidth: { xs: 1500, md: 1600 }, borderCollapse: "separate", borderSpacing: 0 }}>
        <TableHead>
          <TableRow>
            {COLUMNS.map((column) => (
              <TableCell
                key={column.id}
                align={column.align || "center"}
                sx={{
                  ...HEAD_CELL_SX,
                  width: COLUMN_WIDTHS[column.id],
                  maxWidth: COLUMN_WIDTHS[column.id],
                }}
              >
                {column.id === "retail_sector"
                  ? renderFilterHeader({
                      label: column.label,
                      filterKey: "retail_sector",
                      active: Boolean(sectorFilter),
                    })
                  : column.id === "retail_subsector"
                    ? renderFilterHeader({
                        label: column.label,
                        filterKey: "retail_subsector",
                        active: Boolean(subsectorFilter),
                      })
                    : column.id === "country"
                      ? renderFilterHeader({
                          label: column.label,
                          filterKey: "country",
                          active: Boolean(countryFilter),
                        })
                      : column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? orderDirection : "asc"}
                            onClick={() => onSort?.(column.id)}
                            sx={{
                              width: "100%",
                              justifyContent: "center",
                              color: "#ffffff !important",
                              "&:hover": { color: "#ffffff !important" },
                              "&.Mui-active": { color: "#ffffff !important" },
                              "& .MuiTableSortLabel-icon": { color: "#ffffff !important" },
                            }}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody sx={{ background: "white" }}>
          {rows.map((row, index) => {
            const websiteUrl = getRetailWebsiteUrl(row);
            const baseStriped = index % 2 === 0 ? "#fafafa" : "white";

            return (
              <TableRow
                key={row?.id ?? `${row?.concept || "retail"}-${index}`}
                sx={{ ...TABLE_ROW_HOVER_SX, backgroundColor: baseStriped, "&:hover": { backgroundColor: "#eef2f7" } }}
              >
                {COLUMNS.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || "center"}
                  sx={TABLE_STYLES.bodyCell}
                >
                  {column.id === "concept" ? (
                    <Tooltip title={websiteUrl || row?.concept || ""}>
                      <Button
                        variant="text"
                        onClick={
                          websiteUrl
                            ? () => window.open(websiteUrl, "_blank", "noopener,noreferrer")
                            : undefined
                        }
                        sx={{
                          ...TABLE_STYLES.clickableCell,
                          fontWeight: 700,
                          cursor: websiteUrl ? "pointer" : "default",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            maxWidth: COLUMN_WIDTHS.concept,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {getDisplayValue(row, column.id)}
                        </span>
                      </Button>
                    </Tooltip>
                  ) : column.id === "explore" ? (
                    <Tooltip title="Explore company">
                      <IconButton
                        size="small"
                        onClick={(event) => handleOpenExploreMenu(event, row)}
                        sx={{ p: 0.5 }}
                      >
                        <TravelExploreIcon sx={TABLE_ICON_BLUE_SX} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title={String(getDisplayValue(row, column.id))}>
                      <span
                        style={{
                          display: "inline-block",
                          maxWidth: COLUMN_WIDTHS[column.id],
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {getDisplayValue(row, column.id)}
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={exploreAnchorEl}
        open={Boolean(exploreAnchorEl)}
        onClose={handleCloseExploreMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 210,
              borderRadius: 2,
              border: "1px solid rgba(0, 51, 108, 0.12)",
              boxShadow: "0 10px 24px rgba(0, 51, 108, 0.16)",
              mt: 0.5,
            },
          },
        }}
      >
        {exploreActions.map((action) => (
          <MenuItem
            key={action.key}
            sx={TABLE_EXPLORE_MENU_ITEM_SX}
            disabled={Boolean(action.disabled)}
            onClick={() => {
              handleCloseExploreMenu();
              if (!action.disabled) {
                action.onClick();
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>{action.icon}</ListItemIcon>
            <ListItemText primary={action.label} />
          </MenuItem>
        ))}
      </Menu>
      {renderFilterMenu()}
    </>
  );
}
